/**
 * 全屏工具（工位总览 / 工位详情等大屏页面共用）
 *
 * - 优先用浏览器原生 Fullscreen API（真·全屏，连浏览器地址栏一起隐藏）；
 * - 浏览器不支持或调用被拒（比如 iframe 里没加 allowfullscreen）时，
 *   自动退回「CSS 伪全屏」：给根元素挂 .is-fs，固定铺满视口并置顶。
 *
 * 用法：
 *   import { isFullscreen, toggleFullscreen } from './js/fullscreen.js';
 *   toggleFullscreen(this.$el, on => { this.fs = on; });  // on = 切换后是否全屏
 */

const FS_CLASS = 'is-fs'; // 兜底伪全屏时挂在根元素上的类名

/** 当前处于原生全屏的元素（兼容 webkit 老内核） */
function nativeEl() {
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

/** 当前是否全屏（原生 或 兜底伪全屏） */
export function isFullscreen() {
  return !!nativeEl() || !!document.querySelector('.' + FS_CLASS);
}

/**
 * 切换某个元素的全屏状态
 * @param {HTMLElement} el  要全屏的元素（一般传组件根元素 this.$el）
 * @param {(on: boolean) => void} [cb] 切换完成后的回调，on = 切换后是否处于全屏
 */
export function toggleFullscreen(el, cb) {
  if (!el) return;
  const on = !nativeEl() && !el.classList.contains(FS_CLASS);

  if (on) {
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      // 原生 API 可能因为权限 / iframe 限制失败，失败时退回伪全屏
      const p = req.call(el);
      if (p && p.catch) p.catch(() => el.classList.add(FS_CLASS));
    } else {
      el.classList.add(FS_CLASS);
    }
  } else {
    if (nativeEl()) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      const p = exit ? exit.call(document) : null;
      if (p && p.catch) p.catch(() => {});
    }
    el.classList.remove(FS_CLASS);
  }
  // 进出全屏后尺寸变了：手动触发一次 resize，让 echarts / 表格按新尺寸重新排布
  setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
  if (cb) cb(on);
}

/**
 * 监听全屏状态变化（原生全屏按 ESC / F11 退出时不会经过我们的按钮，必须靠这个同步按钮图标）
 * @param {(on: boolean) => void} cb
 * @returns {() => void} 取消监听的函数，组件 beforeDestroy 里调用
 */
export function onFullscreenChange(cb) {
  const handler = () => cb(isFullscreen());
  document.addEventListener('fullscreenchange', handler);
  document.addEventListener('webkitfullscreenchange', handler);
  return () => {
    document.removeEventListener('fullscreenchange', handler);
    document.removeEventListener('webkitfullscreenchange', handler);
  };
}
